import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { AlertCircle, Clock, CheckCircle, XCircle, Calendar, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Issue, User, IssueStatus, IssueType } from '../types';

const ResolutionCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState<(Issue & { assigned_to_user?: User, created_by_user?: User })[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showNewIssueForm, setShowNewIssueForm] = useState(false);
  const [newIssue, setNewIssue] = useState({
    account_email: '',
    assigned_to: '',
    issue_type: 'Revision (email change)' as IssueType,
    comments: '',
    deadline: 24
  });
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [extendingDeadline, setExtendingDeadline] = useState<string | null>(null);
  const [extensionHours, setExtensionHours] = useState<number>(24);

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    let mounted = true;
    let subscription: ReturnType<typeof subscribeToIssues>;

    const loadInitialData = async () => {
      if (!mounted) return;
      
      setLoading(true);
      try {
        const [issuesResult, usersResult] = await Promise.all([
          fetchIssues(),
          fetchUsers()
        ]);

        if (!mounted) return;

        if (issuesResult.data) setIssues(issuesResult.data);
        if (usersResult) setUsers(usersResult);

        subscription = subscribeToIssues();
      } catch (error) {
        console.error('Error loading data:', error);
        if (mounted) toast.error('Failed to load data');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadInitialData();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [user, navigate]);

  const subscribeToIssues = () => {
    return supabase
      .channel('issues-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'issues'
        },
        async () => {
          // Refresh issues when changes occur
          const { data } = await fetchIssues();
          if (data) setIssues(data);
        }
      )
      .subscribe();
  };

  const fetchIssues = async () => {
    let query = supabase
      .from('issues')
      .select(`
        *,
        assigned_to_user:users!issues_assigned_to_fkey(id, name, email, avatar_url),
        created_by_user:users!issues_created_by_fkey(id, name, email, avatar_url)
      `)
      .order('created_at', { ascending: false });

    if (!isAdmin) {
      query = query.eq('assigned_to', user?.id);
    }

    return await query;
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      return [];
    }
  };

  const handleCreateIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      toast.error('Only administrators and supervisors can create issues');
      return;
    }

    if (newIssue.deadline > 24) {
      const proceed = window.confirm('The standard resolution time is 24 hours. Are you sure you want to set a longer deadline?');
      if (!proceed) return;
    }

    try {
      const deadline = new Date();
      deadline.setHours(deadline.getHours() + newIssue.deadline);

      const { error: accountError } = await supabase
        .from('issues')
        .insert([{
          account_email: newIssue.account_email,
          assigned_to: newIssue.assigned_to || null,
          issue_type: newIssue.issue_type,
          comments: newIssue.comments,
          deadline: deadline.toISOString(),
          created_by: user?.id,
          status: 'open' as IssueStatus
        }]);

      if (accountError) throw accountError;

      toast.success('Issue created successfully');
      setShowNewIssueForm(false);
      setNewIssue({
        account_email: '',
        assigned_to: '',
        issue_type: 'Revision (email change)',
        comments: '',
        deadline: 24
      });
      
      const { data } = await fetchIssues();
      if (data) setIssues(data);
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error('Failed to create issue');
    }
  };

  const handleUpdateStatus = async (issueId: string, newStatus: IssueStatus) => {
    setUpdatingStatus(issueId);
    try {
      const { error: updateError } = await supabase
        .from('issues')
        .update({ status: newStatus })
        .eq('id', issueId);

      if (updateError) throw updateError;

      toast.success('Status updated successfully');
      const { data } = await fetchIssues();
      if (data) setIssues(data);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleExtendDeadline = async (issueId: string) => {
    if (!isAdmin) {
      toast.error('Only administrators and supervisors can extend deadlines');
      return;
    }

    if (extensionHours <= 0) {
      toast.error('Please enter a valid number of hours');
      return;
    }

    setExtendingDeadline(issueId);
    try {
      const issue = issues.find(i => i.id === issueId);
      if (!issue) throw new Error('Issue not found');

      const newDeadline = new Date(issue.deadline);
      newDeadline.setHours(newDeadline.getHours() + extensionHours);

      const { error: updateError } = await supabase
        .from('issues')
        .update({ deadline: newDeadline.toISOString() })
        .eq('id', issueId);

      if (updateError) throw updateError;

      toast.success(`Deadline extended by ${extensionHours} hours`);
      const { data } = await fetchIssues();
      if (data) setIssues(data);
      setExtendingDeadline(null);
      setExtensionHours(24);
    } catch (error) {
      console.error('Error extending deadline:', error);
      toast.error('Failed to extend deadline');
    } finally {
      setExtendingDeadline(null);
    }
  };

  const getStatusColor = (status: IssueStatus) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Resolution Center</h1>
            {isAdmin && (
              <button
                onClick={() => setShowNewIssueForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create New Issue
              </button>
            )}
          </div>

          {showNewIssueForm && isAdmin && (
            <div className="mb-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <form onSubmit={handleCreateIssue} className="space-y-4">
                <div>
                  <label htmlFor="account_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Account Email
                  </label>
                  <input
                    type="email"
                    id="account_email"
                    required
                    value={newIssue.account_email}
                    onChange={(e) => setNewIssue(prev => ({ ...prev, account_email: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Assigned To
                  </label>
                  <select
                    id="assigned_to"
                    value={newIssue.assigned_to}
                    onChange={(e) => setNewIssue(prev => ({ ...prev, assigned_to: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="issue_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Issue Type
                  </label>
                  <select
                    id="issue_type"
                    required
                    value={newIssue.issue_type}
                    onChange={(e) => setNewIssue(prev => ({ ...prev, issue_type: e.target.value as IssueType }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="Revision (email change)">Revision (email change)</option>
                    <option value="Revision (phone number change)">Revision (phone number change)</option>
                    <option value="Revision (both email and phone)">Revision (both email and phone)</option>
                    <option value="Lock">Lock</option>
                    <option value="Wrong Password">Wrong Password</option>
                    <option value="Wrong Account Information">Wrong Account Information</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="comments" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Comments
                  </label>
                  <textarea
                    id="comments"
                    rows={4}
                    value={newIssue.comments}
                    onChange={(e) => setNewIssue(prev => ({ ...prev, comments: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Deadline (hours)
                  </label>
                  <input
                    type="number"
                    id="deadline"
                    required
                    min="1"
                    value={newIssue.deadline}
                    onChange={(e) => setNewIssue(prev => ({ ...prev, deadline: parseInt(e.target.value) }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  >
                    Create Issue
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewIssueForm(false)}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            {issues.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500 dark:text-gray-400">No issues found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Account Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Issue Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time Remaining</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Assigned To</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Comments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {issues.map((issue) => (
                      <tr key={issue.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{issue.account_email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{issue.issue_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(issue.status as IssueStatus)}`}>
                            {issue.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {extendingDeadline === issue.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="1"
                                value={extensionHours}
                                onChange={(e) => setExtensionHours(parseInt(e.target.value))}
                                className="w-20 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Hours"
                              />
                              <button
                                onClick={() => handleExtendDeadline(issue.id)}
                                className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setExtendingDeadline(null);
                                  setExtensionHours(24);
                                }}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span>{getTimeRemaining(issue.deadline)}</span>
                              {isAdmin && (
                                <button
                                  onClick={() => setExtendingDeadline(issue.id)}
                                  className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                                >
                                  <Calendar className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <img
                              src={issue.assigned_to_user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(issue.assigned_to_user?.name || issue.assigned_to_user?.email || 'Unknown')}&background=random`}
                              alt="Assignee"
                              className="h-6 w-6 rounded-full mr-2"
                            />
                            <span>{issue.assigned_to_user?.name || issue.assigned_to_user?.email || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {issue.comments}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex space-x-2">
                            {updatingStatus === issue.id ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                            ) : (
                              <>
                                {issue.status !== 'in_progress' && (
                                  <button
                                    onClick={() => handleUpdateStatus(issue.id, 'in_progress')}
                                    className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                                  >
                                    <Clock className="h-5 w-5" />
                                  </button>
                                )}
                                {issue.status !== 'resolved' && (
                                  <button
                                    onClick={() => handleUpdateStatus(issue.id, 'resolved')}
                                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                  >
                                    <CheckCircle className="h-5 w-5" />
                                  </button>
                                )}
                                {issue.status !== 'closed' && (
                                  <button
                                    onClick={() => handleUpdateStatus(issue.id, 'closed')}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    <XCircle className="h-5 w-5" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResolutionCenter;